import React, { Fragment, useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation } from '@apollo/client';
import { gql } from '@apollo/client';
import Header from '../../components/Header';
import Appfooter from '../../components/Appfooter';
import Load from '../../components/Load';
import {
  MEUS_AGENDAMENTOS,
  MEUS_PEDIDOS,
  CANCELAR_AGENDAMENTO,
  STATUS_LABEL,
  STATUS_PEDIDO_LABEL,
  formatCurrency,
} from '../../services/quadraService';

const MEUS_CREDITOS = gql`
  query MeusCreditosPorLocal {
    meusCreditosPorLocal {
      localId
      localNome
      cidadeNome
      saldo
    }
  }
`;

const useCountdown = (targetIso: string | null) => {
  const calc = useCallback(() => {
    if (!targetIso) return null;
    const diff = new Date(targetIso).getTime() - Date.now();
    if (diff <= 0) return null;
    const m = Math.floor(diff / 60000);
    const s = Math.floor((diff % 60000) / 1000);
    return `${m}:${s.toString().padStart(2, '0')}`;
  }, [targetIso]);

  const [remaining, setRemaining] = useState(calc);

  useEffect(() => {
    if (!targetIso) return;
    const interval = setInterval(() => setRemaining(calc()), 1000);
    return () => clearInterval(interval);
  }, [targetIso, calc]);

  return remaining;
};

const contarSlots = (horaInicio: string, horaFim: string, duracaoSlotMinutos: number) => {
  const [hI, mI] = horaInicio.split(':').map(Number);
  const [hF, mF] = horaFim.split(':').map(Number);
  const totalMin = (hF * 60 + mF) - (hI * 60 + mI);
  return Math.round(totalMin / duracaoSlotMinutos);
};

const buildMapsUrl = (local: any): string | null => {
  if (local?.urlLocalizacao) return local.urlLocalizacao;
  const end = local?.endereco;
  if (!end) return null;
  const parts = [end.logradouro, end.numero, end.bairro, end.cidade?.nome, end.cidade?.uf].filter(Boolean);
  if (parts.length === 0) return null;
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(parts.join(', '))}`;
};

const PedidoCard: React.FC<{ pedido: any; onClick: () => void }> = ({ pedido, onClick }) => {
  const st = STATUS_PEDIDO_LABEL[pedido.status] ?? { label: pedido.status, color: 'secondary' };
  const countdown = useCountdown(
    ['ABERTO', 'AGUARDANDO_PAGAMENTO'].includes(pedido.status) ? pedido.reservaExpiraEm : null
  );
  const itensAtivos = pedido.agendamentos.filter((a: any) => a.status !== 'CANCELADO');
  const totalSlots = itensAtivos.reduce((acc: number, a: any) =>
    acc + contarSlots(a.horaInicio, a.horaFim, a.quadra?.duracaoSlotMinutos ?? 30), 0
  );
  const primeiraData = itensAtivos[0]
    ? new Date(itensAtivos[0].dataAgendamento + 'T12:00:00').toLocaleDateString('pt-BR', {
        day: '2-digit', month: 'short', year: 'numeric',
      })
    : '';

  // Pedido pago com data futura = aguardando uso
  const hoje = new Date().toISOString().slice(0, 10);
  const aguardandoUso = pedido.status === 'PAGO' && itensAtivos.some((a: any) => a.dataAgendamento >= hoje);

  const mapsUrl = buildMapsUrl(pedido.local);

  return (
    <div className="card shadow-xss rounded-xxl border-0 mb-3 cursor-pointer" onClick={onClick} style={{ cursor: 'pointer' }}>
      <div className="p-3">
        <div className="d-flex align-items-start justify-content-between mb-2">
          <div>
            <h5 className="fw-700 font-xss mb-0 text-grey-900">
              <i className="feather-shopping-cart me-1 text-primary"></i>
              {pedido.local?.nome}
            </h5>
            <span className="font-xssss text-grey-500">
              {totalSlots} horário{totalSlots !== 1 ? 's' : ''} · {primeiraData}
            </span>
          </div>
          {aguardandoUso ? (
            <div className="d-flex align-items-center gap-1">
              {pedido.agendamentos?.some((a: any) => a.tipoAtor === 'ADMIN') && (
                <span className="badge bg-light text-grey-600 font-xssss rounded-xl">Reservado pelo admin</span>
              )}
              <span className="badge bg-info font-xssss rounded-xl">
                <i className="feather-calendar me-1"></i>Confirmado
              </span>
            </div>
          ) : (
            <div className="d-flex align-items-center gap-1">
              {pedido.agendamentos?.some((a: any) => a.tipoAtor === 'ADMIN') && (
                <span className="badge bg-light text-grey-600 font-xssss rounded-xl">Reservado pelo admin</span>
              )}
              <span className={`badge bg-${st.color} font-xssss rounded-xl`}>{st.label}</span>
            </div>
          )}
        </div>

        <div className="d-flex align-items-center justify-content-between">
          <span className="font-xsss fw-700 text-success">{formatCurrency(pedido.valorTotal)}</span>
          {countdown && (
            <span className="badge bg-warning text-dark font-xssss rounded-xl">
              <i className="feather-clock me-1"></i>Expira em {countdown}
            </span>
          )}
          {aguardandoUso && mapsUrl && (
            <a
              href={mapsUrl}
              target="_blank"
              rel="noreferrer"
              className="font-xssss text-info fw-600 text-decoration-none"
              onClick={(e) => e.stopPropagation()}
            >
              <i className="feather-map-pin me-1"></i>Compareça na quadra
            </a>
          )}
          {aguardandoUso && !mapsUrl && (
            <span className="font-xssss text-info fw-600">
              <i className="feather-map-pin me-1"></i>Compareça na quadra
            </span>
          )}
        </div>

        {/* Lista de horários */}
        {pedido.agendamentos?.length > 0 && (
          <div className="border-top mt-2 pt-2">
            {pedido.agendamentos.map((ag: any) => {
              const dataFmt = new Date(ag.dataAgendamento + 'T12:00:00').toLocaleDateString('pt-BR', {
                weekday: 'short', day: '2-digit', month: 'short',
              });
              const cancelado = ag.status === 'CANCELADO';
              return (
                <div key={ag.id} className={`d-flex align-items-center justify-content-between py-1 ${cancelado ? 'opacity-50' : ''}`}>
                  <span className="font-xssss text-grey-600">
                    {ag.quadra?.nome} · {dataFmt} · {ag.horaInicio} – {ag.horaFim}
                    {cancelado && <span className="badge bg-danger font-xssss rounded-xl ms-1" style={{ fontSize: 9 }}>Cancelado</span>}
                  </span>
                  {ag.valor != null && <span className="font-xssss fw-600 text-success">{formatCurrency(ag.valor)}</span>}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

const AgendamentoCard: React.FC<{
  ag: any;
  onCancelar: (id: string, motivo: string) => Promise<void>;
  onPagar: (id: string) => Promise<void>;
  loadingCancelar: boolean;
  erroPagamento?: string;
}> = ({ ag, onCancelar, onPagar, loadingCancelar, erroPagamento }) => {
  const navigate = useNavigate();
  const [expandido, setExpandido] = useState(false);
  const [motivo, setMotivo] = useState('');
  const [erro, setErro] = useState('');
  const [erroPagar, setErroPagar] = useState('');

  const handlePagarLocal = async () => {
    setErroPagar('');
    try {
      await onPagar(ag.id);
    } catch (e: any) {
      const msg = e?.graphQLErrors?.[0]?.message ?? e?.message ?? 'Erro ao processar pagamento.';
      setErroPagar(msg);
    }
  };

  const countdown = useCountdown(
    ag.status === 'RESERVADO' && ag.reservaExpiraEm ? ag.reservaExpiraEm : null
  );

  const st = STATUS_LABEL[ag.status] ?? { label: ag.status, color: 'secondary' };
  const dataFormatada = new Date(ag.dataAgendamento + 'T12:00:00').toLocaleDateString('pt-BR', {
    weekday: 'short', day: '2-digit', month: 'short', year: 'numeric',
  });

  const reservaExpirada = ag.status === 'RESERVADO' && !countdown;
  const podesPagar = (ag.status === 'RESERVADO' && !reservaExpirada) || ag.status === 'A_PAGAR';
  const podeCancelar = ['RESERVADO', 'A_PAGAR', 'CONFIRMADO'].includes(ag.status);

  const handleCancelar = async () => {
    setErro('');
    try {
      await onCancelar(ag.id, motivo);
    } catch (e: any) {
      setErro(e.message ?? 'Erro ao cancelar.');
    }
  };

  return (
    <div
      className="card shadow-xss rounded-xxl border-0 mb-3"
      style={{ cursor: 'pointer' }}
      onClick={() => navigate(`/quadras/pagamento/${ag.id}`)}
    >
      <div className="p-3">
        <div className="d-flex align-items-start justify-content-between mb-2">
          <div>
            <h5 className="fw-700 font-xss mb-0 text-grey-900">
              <i className="feather-map-pin me-1 text-primary"></i>
              {ag.quadra?.local?.nome}
            </h5>
            <span className="font-xssss text-grey-500">
              {ag.quadra?.nome} · {dataFormatada} · {ag.horaInicio} – {ag.horaFim}
            </span>
          </div>
          <div className="d-flex align-items-center gap-1">
            {ag.tipoAtor === 'ADMIN' && (
              <span className="badge bg-light text-grey-600 font-xssss rounded-xl">
                Reservado pelo admin
              </span>
            )}
            <span className={`badge bg-${st.color} font-xssss rounded-xl`}>{st.label}</span>
          </div>
        </div>

        <div className="d-flex align-items-center justify-content-between">
          <span className="font-xsss fw-700 text-success">{ag.valor != null && ag.valor > 0 ? formatCurrency(ag.valor) : 'Valor pendente'}</span>
          <div className="d-flex gap-2 flex-wrap" onClick={e => e.stopPropagation()}>
            {podesPagar && (
              <button
                className="btn bg-primary-gradiant text-white rounded-xl font-xssss fw-600 px-3 py-1"
                onClick={handlePagarLocal}
              >
                <i className="feather-credit-card me-1"></i> Pagar
              </button>
            )}
            {podeCancelar && (
              <button
                className="btn btn-outline-danger rounded-xl font-xssss fw-600 px-3 py-1"
                onClick={() => setExpandido(v => !v)}
              >
                <i className="feather-x me-1"></i> Cancelar
              </button>
            )}
          </div>
        </div>

        {countdown && (
          <span className="badge bg-warning text-dark font-xssss rounded-xl mt-2">
            <i className="feather-clock me-1"></i>Expira em {countdown}
          </span>
        )}

        {reservaExpirada && (
          <p className="font-xssss text-danger mb-0 mt-2">
            <i className="feather-alert-circle me-1"></i>Reserva expirada
          </p>
        )}

        {erroPagar && (
          <div className="alert alert-warning font-xssss py-2 mt-2 mb-0" onClick={e => e.stopPropagation()}>
            <i className="feather-alert-circle me-1"></i>
            {erroPagar.includes('CPF') ? (
              <>
                CPF não cadastrado.{' '}
                <a href="/athlete/profile" className="fw-bold text-primary text-decoration-underline">
                  Clique aqui para atualizar seu CPF
                </a>
              </>
            ) : erroPagar}
          </div>
        )}
      </div>

      {expandido && (
        <div className="border-top p-3 bg-greylight" style={{ borderRadius: '0 0 15px 15px' }} onClick={e => e.stopPropagation()}>
          <p className="font-xsss fw-600 text-grey-900 mb-2">Confirmar cancelamento</p>
          <textarea
            className="form-control rounded-xl font-xssss mb-2"
            rows={1}
            placeholder="Motivo do cancelamento (opcional)"
            value={motivo}
            onChange={e => {
              setMotivo(e.target.value);
              const el = e.currentTarget;
              el.style.height = 'auto';
              el.style.height = el.scrollHeight + 'px';
            }}
            style={{ resize: 'none', overflow: 'hidden', lineHeight: '1.4' }}
          />
          {erro && <div className="alert alert-danger font-xssss py-2 mb-2">{erro}</div>}
          <div className="d-flex gap-2">
            <button
              className="btn btn-danger rounded-xl font-xssss fw-600 px-3"
              onClick={handleCancelar}
              disabled={loadingCancelar}
            >
              {loadingCancelar ? 'Cancelando...' : 'Confirmar'}
            </button>
            <button
              className="btn btn-light rounded-xl font-xssss fw-600 px-3"
              onClick={() => { setExpandido(false); setErro(''); }}
            >
              Voltar
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

const MeusAgendamentos: React.FC = () => {
  const navigate = useNavigate();
  const [sucesso, setSucesso] = useState('');
  const [abaAtiva, setAbaAtiva] = useState<'ativos' | 'historico'>('ativos');

  const { data, loading, error, refetch } = useQuery(MEUS_AGENDAMENTOS, { fetchPolicy: 'network-only' });
  const { data: pedidosData, loading: loadingPedidos } = useQuery(MEUS_PEDIDOS, { fetchPolicy: 'network-only' });
  const { data: creditosData } = useQuery(MEUS_CREDITOS, { fetchPolicy: 'network-only' });
  const [cancelar, { loading: loadingCancelar }] = useMutation(CANCELAR_AGENDAMENTO);

  const agendamentos: any[] = data?.agendamentosDoAtleta ?? [];
  const pedidos: any[] = pedidosData?.meusPedidosAgendamento ?? [];

  const idsEmPedido = new Set(
    pedidos.flatMap((p: any) => p.agendamentos.map((a: any) => a.id))
  );
  const agendamentosAvulsos = agendamentos.filter(a => !idsEmPedido.has(a.id));

  const handleCancelar = async (id: string, motivo: string) => {
    const { data: res } = await cancelar({ variables: { id, motivo: motivo || undefined } });
    const multa = res.cancelarAgendamento.multaAplicada;
    setSucesso(multa > 0
      ? `Agendamento cancelado. Multa aplicada: ${formatCurrency(multa)}`
      : 'Agendamento cancelado com sucesso.'
    );
    refetch();
  };

  const handlePagar = async (id: string) => {
    navigate(`/quadras/pagamento/${id}`);
  };

  const statusAtivos = ['RESERVADO', 'A_PAGAR', 'CONFIRMADO'];
  const ativos = agendamentosAvulsos.filter(a => statusAtivos.includes(a.status));
  const historico = agendamentosAvulsos.filter(a => !statusAtivos.includes(a.status));

  // Pedidos aguardando pagamento
  const pedidosAguardando = pedidos.filter((p: any) => ['ABERTO', 'AGUARDANDO_PAGAMENTO'].includes(p.status));

  // Pedidos pagos que ainda não foram utilizados (algum agendamento com data futura)
  const hoje = new Date().toISOString().slice(0, 10); // YYYY-MM-DD
  const pedidosConfirmados = pedidos.filter((p: any) =>
    p.status === 'PAGO' &&
    p.agendamentos?.some((a: any) => a.dataAgendamento >= hoje)
  );

  // Pedidos já finalizados (pagos com data passada, cancelados, expirados)
  const pedidosHistorico = pedidos.filter((p: any) =>
    !['ABERTO', 'AGUARDANDO_PAGAMENTO'].includes(p.status) &&
    !pedidosConfirmados.includes(p)
  );

  const pedidosAtivos = [...pedidosAguardando, ...pedidosConfirmados];

  const temConteudo = pedidos.length > 0 || agendamentosAvulsos.length > 0;

  return (
    <Fragment>
      <Header />
      <div className="main-content right-chat-active">
        <div className="middle-sidebar-bottom">
          <div className="middle-sidebar-left pe-0">

            <div className="card shadow-xss w-100 border-0 p-4 mb-3 d-flex flex-row align-items-center justify-content-between">
              <div>
                <h2 className="fw-700 mb-1 font-md text-grey-900">
                  <i className="bi bi-calendar-check text-primary me-2"></i>Meus Agendamentos
                </h2>
                <p className="font-xssss text-grey-500 mb-0">Gerencie suas reservas de quadras</p>
              </div>
              <button
                className="btn bg-primary-gradiant text-white rounded-xl font-xssss fw-600"
                onClick={() => navigate('/quadras')}
              >
                <i className="feather-plus me-1"></i> Nova reserva
              </button>
            </div>

            {sucesso && (
              <div className="alert alert-success font-xsss mb-3 d-flex align-items-center gap-2">
                <i className="feather-check-circle"></i>{sucesso}
                <button className="btn-close ms-auto" style={{ fontSize: 10 }} onClick={() => setSucesso('')} />
              </div>
            )}

            {(loading || loadingPedidos) && <Load />}
            {error && <div className="alert alert-danger font-xsss mb-3">{error.graphQLErrors?.[0]?.message ?? error.message}</div>}

            {!loading && !loadingPedidos && !temConteudo && (
              <div className="card shadow-xss w-100 p-5 text-center border-0">
                <i className="bi bi-calendar-x font-xxl text-grey-300 d-block mb-2"></i>
                <p className="text-grey-500 font-xss mb-2">Você ainda não tem agendamentos.</p>
                <button
                  className="btn bg-primary-gradiant text-white rounded-xl font-xssss fw-600 mx-auto"
                  style={{ width: 'fit-content' }}
                  onClick={() => navigate('/quadras')}
                >
                  Reservar uma quadra
                </button>
              </div>
            )}

            {/* Créditos disponíveis */}
            {creditosData?.meusCreditosPorLocal?.length > 0 && (
              <div className="card shadow-xss rounded-xxl border-0 p-3 mb-3">
                <div className="d-flex align-items-center gap-2 mb-2">
                  <i className="bi bi-wallet2 text-success"></i>
                  <span className="fw-700 font-xsss text-grey-900">Créditos disponíveis</span>
                </div>
                <div className="d-flex flex-wrap gap-2">
                  {creditosData.meusCreditosPorLocal.map((c: any) => (
                    <div
                      key={c.localId}
                      className="bg-greylight rounded-xl px-3 py-2 d-flex align-items-center gap-2"
                      style={{ cursor: 'pointer' }}
                      onClick={() => navigate(`/quadras/local/${c.localId}`)}
                    >
                      <i className="feather-map-pin text-primary" style={{ fontSize: 12 }}></i>
                      <span className="font-xssss text-grey-700">{c.localNome}{c.cidadeNome ? ` — ${c.cidadeNome}` : ''}</span>
                      <span className="font-xssss fw-700 text-success">{formatCurrency(c.saldo)}</span>
                      <i className="feather-chevron-right text-grey-400" style={{ fontSize: 11 }}></i>
                    </div>
                  ))}
                </div>
                <p className="font-xssss text-grey-500 mt-2 mb-0">
                  <i className="feather-info me-1"></i>Créditos são aplicados automaticamente no próximo pagamento do mesmo local.
                </p>
              </div>
            )}

            {/* Abas */}
            {temConteudo && !loading && !loadingPedidos && (
              <>
                <div className="card shadow-xss rounded-xxl border-0 mb-3">
                  <div className="d-flex" style={{ borderBottom: '1px solid #eee', borderRadius: '15px 15px 0 0', overflow: 'hidden' }}>
                    <button
                      className={`btn flex-1 border-0 font-xsss fw-600 py-3 ${abaAtiva === 'ativos' ? 'text-primary' : 'text-grey-500'}`}
                      style={{ borderBottom: abaAtiva === 'ativos' ? '2px solid var(--bs-primary)' : '2px solid transparent', borderRadius: 0 }}
                      onClick={() => setAbaAtiva('ativos')}
                    >
                      <i className="feather-calendar me-1"></i>Ativos
                      {(pedidosAtivos.length + ativos.length) > 0 && (
                        <span className="badge bg-primary rounded-circle ms-1" style={{ fontSize: 10 }}>{pedidosAtivos.length + ativos.length}</span>
                      )}
                    </button>
                    <button
                      className={`btn flex-1 border-0 font-xsss fw-600 py-3 ${abaAtiva === 'historico' ? 'text-primary' : 'text-grey-500'}`}
                      style={{ borderBottom: abaAtiva === 'historico' ? '2px solid var(--bs-primary)' : '2px solid transparent', borderRadius: 0 }}
                      onClick={() => setAbaAtiva('historico')}
                    >
                      <i className="feather-clock me-1"></i>Histórico
                      {(pedidosHistorico.length + historico.length) > 0 && (
                        <span className="badge bg-secondary rounded-circle ms-1" style={{ fontSize: 10 }}>{pedidosHistorico.length + historico.length}</span>
                      )}
                    </button>
                  </div>
                </div>

                {abaAtiva === 'ativos' && (
                  <>
                    {pedidosAtivos.length === 0 && ativos.length === 0 && (
                      <div className="card shadow-xss p-4 text-center border-0">
                        <p className="text-grey-500 font-xss mb-0">Nenhuma reserva ativa no momento.</p>
                      </div>
                    )}

                    {/* Lista unificada: pedidos ativos + avulsas ativas, ordenados por data */}
                    {[
                      ...pedidosAtivos.map((p: any) => ({
                        type: 'pedido' as const,
                        data: p.agendamentos?.[0]?.dataAgendamento ?? p.criadoEm?.slice(0, 10) ?? '',
                        item: p,
                      })),
                      ...ativos.map((ag: any) => ({
                        type: 'avulso' as const,
                        data: ag.dataAgendamento ?? '',
                        item: ag,
                      })),
                    ]
                      .sort((a, b) => a.data.localeCompare(b.data))
                      .map((entry) =>
                        entry.type === 'pedido' ? (
                          <PedidoCard key={entry.item.id} pedido={entry.item} onClick={() => navigate(`/quadras/pedido/${entry.item.id}`)} />
                        ) : (
                          <AgendamentoCard
                            key={entry.item.id}
                            ag={entry.item}
                            onCancelar={handleCancelar}
                            onPagar={handlePagar}
                            loadingCancelar={loadingCancelar}
                          />
                        )
                      )}
                  </>
                )}

                {abaAtiva === 'historico' && (
                  <>
                    {pedidosHistorico.length === 0 && historico.length === 0 && (
                      <div className="card shadow-xss p-4 text-center border-0">
                        <p className="text-grey-500 font-xss mb-0">Nenhum histórico de reservas.</p>
                      </div>
                    )}

                    {/* Lista unificada: pedidos histórico + avulsas histórico, ordenados por data desc */}
                    {[
                      ...pedidosHistorico.map((p: any) => ({
                        type: 'pedido' as const,
                        data: p.agendamentos?.[0]?.dataAgendamento ?? p.criadoEm?.slice(0, 10) ?? '',
                        item: p,
                      })),
                      ...historico.map((ag: any) => ({
                        type: 'avulso' as const,
                        data: ag.dataAgendamento ?? '',
                        item: ag,
                      })),
                    ]
                      .sort((a, b) => b.data.localeCompare(a.data))
                      .map((entry) =>
                        entry.type === 'pedido' ? (
                          <PedidoCard key={entry.item.id} pedido={entry.item} onClick={() => navigate(`/quadras/pedido/${entry.item.id}`)} />
                        ) : (
                          <AgendamentoCard
                            key={entry.item.id}
                            ag={entry.item}
                            onCancelar={handleCancelar}
                            onPagar={handlePagar}
                            loadingCancelar={loadingCancelar}
                          />
                        )
                      )}
                  </>
                )}
              </>
            )}

          </div>
        </div>
      </div>
      <Appfooter />
    </Fragment>
  );
};

export default MeusAgendamentos;
